import React, { useEffect } from "react";
import { Stack, Tabs, Tab, Divider, Theme } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { AddButton } from "./FormLayout/IconButtons/AddButton";

const tabHeight = "34px";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      paddingTop: 0,
      paddingBottom: theme.spacing(1),
      backgroundColor: "inherit",
    },
    tabs: {
      minHeight: tabHeight,
      height: tabHeight,
    },
    tab: {
      minHeight: tabHeight,
      height: tabHeight,
    },
    tabPanel: {
      display: "flex",
      justifyContent: "center",
      "& > *": {
        padding: theme.spacing(1),
        width: "100%",
        height: "100%",
      },
    },
  }),
);

interface TabViewProps {
  children?: React.ReactNode;
  tabLabels: string[] | React.ReactNode[] | undefined;
  activeTabIndex?: number;
  disableInactiveTabs?: boolean;
  onTabChange?: (newIndex: number) => void;
  addButtonEnabed?: boolean;
  onAddButtonClicked?: () => void;
  disableScrollButtons?: boolean;
}

export function TabView(props: TabViewProps) {
  const classes = useStyles();
  const [selectedTabIndex, setSelectedTabIndex] = React.useState<number>(0);

  useEffect(() => {
    if (props.activeTabIndex !== undefined) setSelectedTabIndex(props.activeTabIndex);
  }, [props.activeTabIndex]);

  const handleChange = (event: React.ChangeEvent<{}>, newIndex: number) => {
    setSelectedTabIndex(newIndex);
    props.onTabChange && props.onTabChange(newIndex);
  };

  return (
    <React.Fragment>
      <Stack direction="row">
        <Tabs
          value={selectedTabIndex}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons={props.disableScrollButtons ? false : "auto"}
        >
          {props.tabLabels?.map((label: string | React.ReactNode, index: number) => (
            <Tab
              key={`tab-${typeof label === "string" ? label : index}`}
              label={label}
              classes={{ root: classes.tab }}
              disabled={props.disableInactiveTabs && selectedTabIndex !== index}
              sx={{ textTransform: "none" }}
            />
          ))}
        </Tabs>
        {props.addButtonEnabed && <AddButton onClick={props.onAddButtonClicked} />}
      </Stack>
      <Divider variant="fullWidth" />
      {props.children &&
        React.Children.map(props.children, (child, index) => {
          return (
            <TabPanel selectedIndex={selectedTabIndex} index={index}>
              {" "}
              {child}
            </TabPanel>
          );
        })}
    </React.Fragment>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  selectedIndex: any;
}

function TabPanel(props: TabPanelProps) {
  const classes = useStyles();
  const { children, selectedIndex, index, ...other } = props;

  return (
    <div hidden={selectedIndex !== index} {...other} className={classes.tabPanel}>
      {selectedIndex === index && children}
    </div>
  );
}
